import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactCountryFlag from 'react-country-flag';
import Moment from 'moment';
import clsx from 'clsx';
import ifEmoji from 'if-emoji';

import { makeStyles, useTheme } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';

import FavoriteIcon from '@mui/icons-material/Favorite';

import CountryFlag from './country-flag';

import IPAddress from './ipaddress';
import FileHash from './filehash';
import Tooltip from './styled/tooltip';

import { lavender } from '../data/colors';


const useStyles = makeStyles(theme => ({
    data: {
        display: 'inline-flex',
        padding: theme.spacing(1, 0, 0, 1),
        overflowY: 'hidden',
        minHeight: 30.5,
        '& i': {
            fontStyle: 'normal',
        },
    },
    value: {
        overflowWrap: 'anywhere',
        textAlign: 'left',
        paddingLeft: 5
    },
    valuesContainer: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    noClickyNoLinky: {
        ...theme.typography.button,
        color: theme.palette.primary.main,
        minWidth: 26,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 5,
        fontSize: 15,
        // maxHeight: 20,
        textTransform: 'none',
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-all',
        textOverflow: 'ellipsis',
        lineHeight: 1.3,
    },
    indexedValue: {
        ...theme.typography.button,
        cursor: 'pointer',
        minWidth: 26,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 5,
        fontSize: 15,
        // maxHeight: 20,
        textTransform: 'none',
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-all',
        textOverflow: 'ellipsis',
        lineHeight: 1.3,
    },
    favorite: {
        minWidth: 26,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: 15,
        maxHeight: 20,
    },
    label: {
        color: theme.palette.primary.label,
        whiteSpace: 'nowrap',
        '&:hover': {
            cursor: 'pointer',
        },
        '&.info1': {
            // color: theme.palette.tertiary.main,
            color: lavender[400],
            // color: olive[400],
        },
        '&.auto': {
            textTransform: 'capitalize'
        }
    },
    nestedLabel: {
        opacity: 0.7,
        whiteSpace: 'nowrap',
    },
    flag: {
        lineHeight: 'initial !important',
        // paddingLeft: 5,
        // height: 20,
        // width: 20,
    }
}));

const toArray = d => Array.isArray(d) ? d : [d];

// default delimiter is '.'
// support multiple delimiters. everything before the last delimiter is omitted from the label
// we'll also convert '_' to spaces
const label = (k, d) => {
    const delimiter = toArray(d);
    const ret = delimiter.reduce((a, c) => (a).split(c).slice(-1).pop(), k || '');
    return ret.replaceAll('_', ' ');
};

const KeyValuePair = ({ delimiter = '.', ...props }) => {
    const classes = useStyles();

    const handleOnValueClick = (t, d) => {
        props.onClick && props.onClick(t, d);
    };

    return (
        <div className={classes.data}>
            {props.onFavorite && <IconButton
                aria-label="favorite"
                className={classes.favorite}
                size="small"
                disabled={!props.favorite}
                color="secondary">
                {props.favorite && <FavoriteIcon color="secondary" fontSize="inherit" />}
            </IconButton>}
            {props.titleComponent
                ? React.cloneElement(props.titleComponent, { onClick: props.onFavorite })
                : <i className={clsx(classes.label, {
                    info1: props.info === 1,
                    auto: !props.title // don't transform labels that have custom titles
                })}
                    title={props.titleTip}
                    onClick={props.onFavorite}>
                    {props.icon}
                    {props.title || label(props.path, delimiter)}:
                </i>
            }
            <Value delimiter={delimiter} {...props} onClick={props.onClick ? handleOnValueClick : undefined} />
        </div>
    );
};

KeyValuePair.propTypes = {
    path: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
    ]),
    type: PropTypes.string,
    info: PropTypes.number,
    indexed: PropTypes.bool,
    favorite: PropTypes.bool,
    onFavorite: PropTypes.func,
    delimiter: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    onClick: PropTypes.func,
    // used for mapped columns,
    // if data is array tip will be array of same length
    tip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    titleTip: PropTypes.string,
    // node to use for label
    titleComponent: PropTypes.node,
    // optional icon
    icon: PropTypes.node,
};
const hasEmojiSupport = ifEmoji('🇺🇸');

const Value = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [data, setData] = useState('');
    const [isTruncated, setIsTruncated] = useState(false);
    const [emoji] = useState(hasEmojiSupport);

    // we can add more special formatting here
    const format = d => {
        if (typeof d === 'boolean') {
            return d ? 'true' : 'false';
        }

        if (props.type === 'str') {
            if (props.path && (props.path.endsWith('.proto') || props.path === 'proto')) {
                return d.toUpperCase();
            }
            return d;
        }

        if (!props.type) {
            return d;
        }
        if (props.type === 'date') {
            return toArray(d).map(dd => Moment(dd).format(theme.date.long)).join(', ');
        }
        if (props.type === 'bool') {
            return d ? 'true' : 'false';
        }

        return d;
    };

    const handleOnValueClick = (e, d) => {
        e.stopPropagation();
        props.onClick && props.onClick(e.currentTarget, d);
    };

    const isCountry = () => props.path.endsWith('_location.country');

    const isObject = d => d instanceof Object;

    useEffect(() => {
        const data = JSON.parse(JSON.stringify(
            props.data !== undefined && props.data !== null ? props.data : ''
        ));
        // defense the dark arts of thousands of flow uuids
        // entire array will stil be available in the json tab
        if (Array.isArray(props.data)) {
            setIsTruncated(props.data.length > 111);
            data.length = Math.min(props.data.length, 111);
        }
        setData(data);
    }, [props.data]);

    return (
        <span className={classes.valuesContainer}>
            {(toArray(data) || []).map((d, i) => {
                const tip = toArray(props.tip);
                return <React.Fragment key={`${d}_${i}`}>
                    {/* ip control with filters */}
                    {props.ip && props.onClick &&
                        <Link
                            color="primary"
                            onClick={e => handleOnValueClick(e, d)}
                            className={classes.indexedValue}>
                            <IPAddress
                                tipPrefix={tip.length >= i && tip[i] ? `${tip[i]} ` : ''}
                                country={props.country}
                                address={d}
                                uuid={props.sensor} />
                        </Link>
                    }
                    {/* ip control without filters */}
                    {props.ip && !props.onClick &&
                        <div style={{ color: theme.palette.primary.main }}>
                            <IPAddress
                                country={props.country}
                                address={d}
                                uuid={props.sensor}
                                tipPrefix={tip.length >= i && tip[i] ? `${tip[i]} ` : ''} />
                        </div>
                    }
                    {/* file_hash with filters */}
                    {props.fileHash && props.onClick &&
                        <Link
                            color="primary"
                            onClick={e => handleOnValueClick(e, d)}
                            className={classes.indexedValue}>
                            <FileHash hash={d} title={props.title} />
                        </Link>
                    }
                    {/* file_hash without filters */}
                    {props.fileHash && !props.onClick &&
                        <div style={{ color: theme.palette.primary.main }}>
                            <FileHash hash={d} title={props.title} />
                        </div>
                    }
                    {/* country flag */}
                    {isCountry() && emoji &&
                        <span style={{ paddingLeft: 5 }}>
                            <CountryFlag country={d} />
                        </span>
                    }
                    {isCountry() && !emoji &&
                        <span style={{ paddingLeft: 5 }}>
                            <CountryFlag country={d} />
                        </span>
                    }
                    {/* nested objects */}
                    {isObject(d) &&
                        <div>
                            {Object.entries(d).map(([k, v], j) => (
                                <div key={`${k}_${i}`} style={{ paddingLeft: 10 }}>
                                    <span className={classes.nestedLabel}>{label(k, props.delimiter)}:</span>
                                    <span style={{ paddingLeft: 5 }}>{format(v)}</span>
                                </div>
                            ))}
                        </div>
                    }
                    {/* regular non-indexed value */}
                    {!isObject(d) && !props.indexed && !props.ip && !props.fileHash &&
                        <span className={classes.value}>{format(d)}</span>
                    }
                    {/* indexed value with link */}
                    {!isObject(d) && props.indexed && !props.ip && !props.fileHash && props.onClick &&
                        <MaybeTip tip={tip.length >= i && tip[i] ? `${tip[i]}` : ''}>
                            <Link
                                color="primary"
                                onClick={e => handleOnValueClick(e, d)}
                                className={classes.indexedValue}>
                                {`${format(d)}`}
                            </Link>
                        </MaybeTip>
                    }
                    {/* indexed value without filters (incidents data) */}
                    {!isObject(d) && props.indexed && !props.ip && !props.fileHash && !props.onClick &&
                        <span className={classes.noClickyNoLinky}>{format(d)}</span>
                    }
                </React.Fragment>;
            })}{isTruncated ? '...' : ''}
        </span>
    );
};

Value.propTypes = {
    data: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
    ]),
    path: PropTypes.string.isRequired,
    type: PropTypes.string,
    indexed: PropTypes.bool,
    onClick: PropTypes.func,
    country: PropTypes.string,
    sensor: PropTypes.string,
    delimiter: PropTypes.string,
    ip: PropTypes.bool,
    fileHash: PropTypes.bool,
    title: PropTypes.string,
    tip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
};

const MaybeTip = (props) => {
    return props.tip
        ? <Tooltip title={props.tip} arrow placement="top" {...props} />
        : <React.Fragment> {props.children} </React.Fragment>;
};

MaybeTip.propTypes = {
    tip: PropTypes.string,
    children: PropTypes.node,
};

export default KeyValuePair;
